import "./ConversationHeader.css"
import { useState, useEffect } from "react";
import ModalManager from "@ebay/nice-modal-react";
import ApiModal from "/src/components/modal/ApiModal/ApiModal";
import { useToastDispatchContext } from "/src/layouts/toast/context/toastContext";
import ConversationCompletedService from "../../../services/ConversationCompletedService";
import storage from "/src/utils/storage";
import reviewerColor from "/src/utils/reviewerColor";
import hasPermission from "/src/utils/hasPermission";
import useUnitLabels from "/src/hooks/useUnitLabels";
import canMakeReview from "/src/features/supervision/conversations/utils/canMakeReview";
function ConversationHeader({ conversation, setConversation, presentUsers = [] }) {
    const toast = useToastDispatchContext();
    const { getLabel: getUnitLabel } = useUnitLabels();
    const date = new Date(conversation.timestamp || conversation.created_at);

    const [globalComment, setGlobalComment] = useState(conversation?.comment || "");

    const { updateConversation, resetConversationReview } = ConversationCompletedService;

    const user = storage('local').get("user");
    const canMarkAsReviewed = user?.is_admin || hasPermission('review_conversation');
    const canReopenReview = user?.is_admin || hasPermission('reopen_conversation');
    const canUpdateConversation = canMarkAsReviewed || canReopenReview;
    const canResetConversation = user?.is_admin || hasPermission('reset_conversation');
    const canMakeReviewPermission = canMakeReview();
    const canViewReviewed = user?.is_admin || hasPermission('view_reviewed');
    const canViewReviewing = user?.is_admin || hasPermission('view_reviewing');

    const created_at_formatted =
        date.toLocaleDateString("fr-FR") + " " +
        date.toLocaleTimeString("fr-FR", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false
        });

    const copy_value = (value) => {
        navigator.clipboard.writeText(value).then((_) => {
            toast.success("Copy to clipboard", "You can paste this value where you need !", 5000);
        })
    }

    const formatKey = (key) => key.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());

    const handleGlobalCommentSave = async () => {
        if (globalComment === (conversation?.comment || "")) {
            return;
        }

        const [response, error] = await updateConversation(conversation.call_id, {
            comment: globalComment
        });

        if (error) {
            toast.error("Error", "Failed to save comment");
            return;
        }

        conversation.comment = globalComment
        toast.success("Comment Saved", "The global comment has been saved successfully.");
    };

    const handleReview = async (close) => {
        const [response, error] = await updateConversation(conversation.call_id, {
            is_closed: close
        });

        setConversation(prev => ({
            ...prev,
            data: {
                ...prev.data,
                is_closed: close
            }
        }));

        if (error) {
            toast.error("Error", "Failed to review the conversation");
            return;
        }
        let comment = close ? "The conversation has been reviewed." : "The conversation review has been reopened.";
        toast.success("Review Saved", comment);
    }

    const handleResetConversation = async () => {
        const response = await ModalManager.show(ApiModal, {
            title: "Reset conversation " + conversation.call_id,
            body: "This permanently deletes all feedback, per-criteria comments, manual result overrides, \"not applicable\" markings and manually added criteria, and clears the global assessment and reviewed state. The AI's own results are kept. This cannot be undone.",
            api: {
                request: resetConversationReview,
                param: [conversation.call_id],
            },
            buttons: [
                { key: "cancel", className: "cancel", label: "Cancel" },
                { key: "validate", className: "validate", label: "Reset", type: "submit" },
            ],
            confirm: {
                name: conversation.call_id,
                fieldName: "the conversation",
            },
        });

        if (!response) return;

        toast.success("Conversation Reset", "The conversation's review has been reset.");
        window.location.reload();
    }

    useEffect(() => {
        setGlobalComment(conversation?.comment || "");
    }, [conversation]);
    return (
        <div className="conversation_profil_header">
            <h3> {conversation?.meta_data?.call_direction + " - "} {conversation?.meta_data?.contact_id} - {conversation?.agent_id?.split("@")[0]} </h3>
            <br></br>
            <div className="conversation_profil_header_infos">
                <div className="conversation_profil_row"> <i className="fa-solid fa-user-group"></i> <span> {getUnitLabel(conversation?.unit_id)}</span></div>
                <div className="conversation_profil_row"> <i className="fa-solid fa-headset"></i> <span> {conversation?.agent_id}</span></div>
                <div className="conversation_profil_row"> <i className="fa-solid fa-user"></i> <span>{conversation?.meta_data?.contact_id}</span></div>
                <div className="conversation_profil_row"> <i className="fa-solid fa-calendar-days"></i> <span>{created_at_formatted}</span></div>
                {(canViewReviewed || canViewReviewing) && (
                    <div className="conversation_profil_row">
                        <i className="fa-solid fa-user-pen"></i>
                        {canViewReviewed && (
                            conversation?.reviewers?.length > 0 ? (
                                conversation.reviewers.map((reviewer) => (
                                    <span
                                        key={reviewer.email}
                                        className="reviewer_chip"
                                        style={{ background: reviewerColor(reviewer.email) }}
                                        title="Click to copy email"
                                        onClick={() => copy_value(reviewer.email)}
                                    >
                                        {reviewer.name || reviewer.email}
                                    </span>
                                ))
                            ) : (
                                <span className="reviewers_empty">Empty</span>
                            )
                        )}
                        {canViewReviewing && presentUsers.map((reviewer) => (
                            <span
                                key={reviewer.email}
                                className="reviewer_chip reviewer_chip--live"
                                style={{ background: reviewerColor(reviewer.email) }}
                                title="Click to copy email"
                                onClick={() => copy_value(reviewer.email)}
                            >
                                {reviewer.name || reviewer.email}
                            </span>
                        ))}
                    </div>
                )}
                <br></br>
                <div className="conversation_profil_container_metadata">
                    <div className="conversation_profil_container_metadata">
                        <div key="call_id" className="metadata_chip purple" onClick={() => copy_value(conversation?.call_id)}>
                            <strong>{"Call id:"}</strong>&nbsp;{conversation?.call_id}
                        </div>

                        {Object.entries(conversation?.meta_data).map(([key, value]) => (
                            <div key={key} className="metadata_chip red" onClick={() => copy_value(Array.isArray(value) ? value.join(", ") : value)}>
                                <strong>{formatKey(key)}:</strong>&nbsp;{Array.isArray(value) ? value.join(", ") : value}
                            </div>
                        ))}
                    </div>
                </div>

                <div className="conversation_global_note">
                    <div className="conversation_global_note_header">
                        <span>Global assessment</span>
                        <div className="conversation_global_note_actions">
                            {!canMakeReviewPermission && (
                                <span className="conversation_view_only" title="You can see this conversation but not modify it">
                                    <i className="fas fa-eye"></i> View only
                                </span>
                            )}
                            {canMakeReviewPermission && canResetConversation && (
                                <div className="button_review button_review_danger" onClick={handleResetConversation}>
                                    Reset conversation
                                </div>
                            )}
                            {!conversation.is_closed ? (
                                canMakeReviewPermission && canMarkAsReviewed && (
                                    <div className="button_review" onClick={() => handleReview(true)}>
                                        Mark as reviewed
                                    </div>
                                )
                            ) : (
                                canMakeReviewPermission && canReopenReview && (
                                    <div className="button_review button_review_revert" onClick={() => handleReview(false)}>
                                        Reopen review
                                    </div>
                                )
                            )}
                        </div>
                    </div>

                    <textarea
                        className="conversation_global_note_input"
                        placeholder="Write a global comment about this conversation..."
                        value={globalComment}
                        onChange={(e) => setGlobalComment(e.target.value)}
                        onBlur={() => handleGlobalCommentSave()}
                        disabled={conversation.is_closed || !canUpdateConversation || !canMakeReviewPermission}
                    />
                </div>
            </div>
        </div>
    );
}
export default ConversationHeader;